import React from 'react';
import { CommandBar, ICommandBarItemProps, Label, MessageBar, Stack, TextField } from '@fluentui/react';
import { toast } from 'react-toastify';
import ToggleField from '../../Common/ToggleField';
import DataField from '../../Common/DataField';
import NumberField from '../../Inputs/NumberField';
import { ISmtpSettings, ISmtpSetupRequest } from '../../../models/Storefront';
import { useGetSmtpSettingsQuery, usePatchSmtpMutation } from '../../../store/endpoints/StorefrontSettingsEndpoints';
import SmtpTestDialog from './SmtpTestDialog';
import EmailLogsPanel from './EmailLogsPanel';
import Setting, { DynamicSetting } from '../../Common/Setting';

const DEBOUNCE_MS = 1200;

/**
 * "Email" integration pivot: send storefront emails through the tenant's own SMTP server. All settings live
 * inline on the page and are PATCHed as they change (debounced). The command bar exposes a Test action (send
 * a test email) and a Logs action (open the email activity log panel).
 */
const EmailIntegration: React.FC = () => {
    const settingsQuery = useGetSmtpSettingsQuery();
    const [patchSmtp] = usePatchSmtpMutation();

    const [local, setLocal] = React.useState<ISmtpSettings>({});
    const [password, setPassword] = React.useState<string>('');
    const originalRef = React.useRef<ISmtpSettings>();
    const pendingRef = React.useRef<ISmtpSetupRequest>({});
    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const [testOpen, setTestOpen] = React.useState<boolean>(false);
    const [logsOpen, setLogsOpen] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (settingsQuery.data && !originalRef.current) {
            setLocal(settingsQuery.data);
            originalRef.current = settingsQuery.data;
        }
    }, [settingsQuery.data]);

    const doSave = React.useCallback(async () => {
        const patch = pendingRef.current;
        pendingRef.current = {};
        if (Object.keys(patch).length === 0) return;
        const toastId = toast.loading('Saving email settings…');
        try {
            const res = await patchSmtp(patch).unwrap();
            if (res.success) {
                toast.update(toastId, { type: 'success', render: 'Email settings saved', isLoading: false, autoClose: 2500 });
                // The password is write-only; once saved it lives only in Key Vault, so clear the field.
                if (patch.password) setPassword('');
            } else {
                toast.update(toastId, { type: 'error', render: res.message || 'Failed to save email settings', isLoading: false, autoClose: 4000 });
            }
        } catch {
            toast.update(toastId, { type: 'error', render: 'Failed to save email settings', isLoading: false, autoClose: 4000 });
        }
    }, [patchSmtp]);

    const scheduleSave = React.useCallback(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(doSave, DEBOUNCE_MS);
    }, [doSave]);

    // Flush any pending change on unmount so a quick navigate-away doesn't lose the last edit.
    React.useEffect(() => () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (Object.keys(pendingRef.current).length > 0) patchSmtp(pendingRef.current);
    }, [patchSmtp]);

    const change = React.useCallback(<K extends keyof ISmtpSettings>(field: K, value: ISmtpSettings[K]) => {
        setLocal(prev => ({ ...prev, [field]: value }));
        (pendingRef.current as Record<string, unknown>)[field] = value;
        scheduleSave();
    }, [scheduleSave]);

    const changePassword = React.useCallback((value: string) => {
        setPassword(value);
        // Blank means "keep the current password" (the API ignores it) — don't queue a pointless save.
        if (value) {
            pendingRef.current.password = value;
            scheduleSave();
        } else {
            delete pendingRef.current.password;
        }
    }, [scheduleSave]);

    const commands: ICommandBarItemProps[] = [
        {
            key: 'test',
            text: 'Test',
            iconProps: { iconName: 'Mail' },
            disabled: !settingsQuery.data?.configured,
            title: settingsQuery.data?.configured ? 'Send a test email' : 'Save an SMTP password before testing',
            onClick: () => setTestOpen(true),
        },
        {
            key: 'logs',
            text: 'Logs',
            iconProps: { iconName: 'History' },
            onClick: () => setLogsOpen(true),
        },
    ];

    /*
     * The id has to come from the field, not the label: this renders four times, once per email
     * kind, and a shared id would mean a link to "From Address" always highlighted the first pair
     * while the other three were unreachable. `findSettingElement` takes the first match.
     */
    const fromPair = (label: string, addrField: keyof ISmtpSettings, nameField: keyof ISmtpSettings) => (
        <>
            <Label>{label}</Label>
            <Stack horizontal tokens={{ childrenGap: '1em' }}>
                <Stack.Item grow>
                    <DynamicSetting id={`storefront.smtp.${String(addrField)}`}>
                        <DataField label='From Address' name={`smtp-${String(addrField)}`} value={(local[addrField] as string) ?? ''} onChange={(_e, v) => change(addrField, (v ?? '') as never)} />
                    </DynamicSetting>
                </Stack.Item>
                <Stack.Item grow>
                    <DynamicSetting id={`storefront.smtp.${String(nameField)}`}>
                        <DataField label='From Name' name={`smtp-${String(nameField)}`} value={(local[nameField] as string) ?? ''} onChange={(_e, v) => change(nameField, (v ?? '') as never)} />
                    </DynamicSetting>
                </Stack.Item>
            </Stack>
        </>
    );

    return (
        <Stack tokens={{ childrenGap: '1em' }}>
            <CommandBar items={commands} styles={{ root: { paddingLeft: 0 } }} />
            <MessageBar>Send storefront emails (login, order, quote, and prepared cart) through your own SMTP server instead of the default provider. Sends fall back to the default provider if the SMTP server rejects a message.</MessageBar>

            <ToggleField
                settingId='storefront.smtp.enabled'
                inline
                label='Enable Custom SMTP'
                checked={local.enabled ?? false}
                onChange={(_e, v) => change('enabled', v ?? false)}
            />
            <DataField settingId='storefront.smtp.host' label='Host' name='smtp-host' value={local.host ?? ''} onChange={(_e, v) => change('host', v ?? '')} />
            <NumberField settingId='storefront.smtp.port' label='Port' name='smtp-port' value={local.port} onChange={(v) => change('port', v)} />
            <ToggleField settingId='storefront.smtp.useTls' inline label='Use TLS' checked={local.useTls ?? false} onChange={(_e, v) => change('useTls', v ?? false)} />
            <DataField settingId='storefront.smtp.username' label='Username' name='smtp-username' value={local.username ?? ''} onChange={(_e, v) => change('username', v ?? '')} />
            <Setting id='storefront.smtp.password'>
                <TextField
                    label='Password'
                    name='smtp-password'
                    type='password'
                    canRevealPassword
                    value={password}
                    onChange={(_e, v) => changePassword(v ?? '')}
                    placeholder={settingsQuery.data?.configured ? 'Configured — leave blank to keep current password' : 'Enter password'}
                />
            </Setting>

            {fromPair('Login Email', 'fromLogin', 'fromLoginName')}
            {fromPair('Order Email', 'fromOrder', 'fromOrderName')}
            {fromPair('Quote Request Email', 'fromQuote', 'fromQuoteName')}
            {fromPair('Prepared Cart Email', 'fromPreparedCart', 'fromPreparedCartName')}

            <SmtpTestDialog isOpen={testOpen} onDismiss={() => setTestOpen(false)} />
            <EmailLogsPanel isOpen={logsOpen} onDismiss={() => setLogsOpen(false)} />
        </Stack>
    );
};

export default EmailIntegration;
